/**
 * Стандартная страница оплаты: её видит любой мерчант, которому не назначен персональный шаблон
 * (см. `src/templates/index.ts`).
 *
 * Как она устроена:
 *  - реквизиты и загрузка чека живут на одном экране, шага в прогрессе два, а не три;
 *  - «Я оплатил» — финальное действие: отправляет чек и подтверждает оплату;
 *  - при первом показе экрана оплаты страница отмечает «клиент платит»
 *    (`POST /api/receipt-intent` через `setShowConfirm`), что даёт тикету 20 минут вместо 10.
 *    Отметку шлём только там, где окно действительно короткое — то есть когда требуется
 *    чек (матчинг); остальным она ничего не меняет, а экран таймаута сдвинула бы.
 *
 * Раннего `confirm-transaction` здесь намеренно нет: он переводит тикет в `FILES_VERIFICATION`
 * (переход `T_CONFIRMED_BY_CLIENT`), то есть отправил бы его оператору без чека и увёл клиента
 * с экрана оплаты. Окно продлевает отметка перехода к чеку, а не подтверждение оплаты.
 */
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { TemplateProps } from '../types';
import OnePagePaymentPage from './pages/OnePagePaymentPage';
import OnePageUploadPage from './pages/OnePageUploadPage';
import OnePageSuccessPage from './pages/OnePageSuccessPage';
import OnePageRetryPage from './pages/OnePageRetryPage';
import {
  OnePageProcessingScreen,
  OnePageReceiptUnconfirmedScreen,
  OnePagePaymentExpiredScreen,
  OnePageErrorScreen,
  OnePageExpiredScreen,
  OnePageServerErrorScreen,
} from './pages/OnePageProcessingPage';
import Spinner from '../../components/Spinner';
import OnePageVariantPage from './pages/OnePageVariantPage';
import { supportTarget } from '../../services/supportUrl';

// Methods we serve — must NOT be bounced to the merchant before the search resolves. Whether the
// client pays on our page (card/SBP requisite) or gets redirected to the provider (redirect_url +
// paymentUrl) is decided by the requisite data at PAYMENT_PENDING, not by this list (CPAYMENT-1661).
const SUPPORTED_METHODS = [
  'SBP P2P RUB',
  'SBP MATCHING RUB',
  'TRANS SBP P2P RUB',
  'OZON SBP RUB',
  'ALFA SBP RUB',
  'SBER SBP RUB',
  'VTB SBP RUB',
  'TBANK SBP RUB',
  'MC/VISA/MIR P2P RUB',
  'P2P MATCHING RUB',
  'MC/VISA P2P KZT',
  'MOBILE P2P KZT',
  'MC/VISA P2P KRW',
  'MC/VISA UAH',
  'PRIVAT24 P2P UAH',
  'M10AZN',
  'P2P QUASI ECOM RUB',
  'MATCHING QUASI ECOM RUB',
  'SBER TRANS P2P RUB',
  'TBANK TRANS P2P RUB',
  'TBANK TRANS SBP P2P RUB',
  'VTB TRANS P2P RUB',
  'YOOMONEY TRANS P2P RUB',
  'SBER TRANS QR P2P RUB',
];

// Redirect methods: the client pays on an external partner page (redirect_url requisite), and the
// deposit may first show the amount tile (WAITING_FOR_VARIANT_SELECTION). They must NOT be bounced
// back to the merchant at CLIENT_PENDING before the search resolves — the tile/requisite would be
// skipped. Payment step still goes to the provider via the redirect_url branch (CPAYMENT-1619).
const REDIRECT_METHODS = ['P2P QUASI ECOM RUB', 'MATCHING QUASI ECOM RUB'];

const REQUISITE_STATUSES = ['WAITING_FOR_SMS', 'PAYMENT_PENDING'];

const LionOnePageTemplate = ({
  transaction,
  loading,
  error,
  timeLeft,
  timeoutScreen,
  uuid,
  receiptIntent,
  lowTimeWarning,
  onExtendTime,
  onDismissLowTime,
  onConfirm,
  onCancel,
  onDeclineOffer,
  cancelReasons,
  onLeave,
  onRedirect,
  onSelectVariant,
  variantSubmitting,
  setRetry,
  setShowConfirm,
  formatTime,
}: TemplateProps) => {
  const { t } = useTranslation();
  const [receiptAccepted, setReceiptAccepted] = useState(false);
  const [processingAfterUpload, setProcessingAfterUpload] = useState(false);
  const [serverError, setServerError] = useState(false);
  // CPAYMENT-1616: bridge the ~3s until the poll clears a stale "failed" after a re-upload.
  const [reuploading, setReuploading] = useState(false);
  // Отметка «клиент платит» уходит один раз за сессию страницы — повторные рендеры её не дублируют.
  const earlyIntentSentRef = useRef(false);

  const formattedTime = formatTime(timeLeft ?? 600);

  const isSupportedMethod = SUPPORTED_METHODS.includes(transaction?.paymentMethod ?? '');
  const isRedirectMethod = REDIRECT_METHODS.includes(transaction?.paymentMethod ?? '');

  const shouldRedirectNoRequisite =
    !!transaction &&
    transaction.status === 'CLIENT_PENDING' &&
    !transaction.requisite?.type &&
    !isSupportedMethod &&
    !isRedirectMethod &&
    (transaction as any).paymentAdapterSearchAttemptsCounter === 0;

  useEffect(() => {
    if (shouldRedirectNoRequisite && transaction?.redirectUrl) {
      onRedirect(transaction.redirectUrl);
    }
  }, [shouldRedirectNoRequisite, transaction?.redirectUrl, onRedirect]);

  /**
   * Окно оплаты — с самого показа реквизитов. Клиент этого экрана уже стоит перед формой загрузки
   * чека, поэтому отметку «пошёл за чеком» ставим сразу, а не по клику: короткие 10 минут гасили бы
   * тикет, пока клиент ищет чек в приложении банка (механика CPAYMENT-1716). Длительность выбирает
   * бэкенд по флагу `onePage` — здесь это 20 минут (CPAYMENT-1753), своим таймером их не считаем.
   *
   * `setShowConfirm` — единственная точка входа на экран чека во всех шаблонах, отметку шлёт она.
   *
   * Редиректные методы (quasi-ecom) сюда не попадают: чек грузят не у нас, клиент вообще не
   * задерживается на этом экране — он уходит на чекаут провайдера ещё до того, как здесь успевает
   * что-то отрисоваться. Отметка для них не только бессмысленна (`external_user_receipt_intent_date`
   * без реального намерения грузить чек), но и опасна: её асинхронный ответ обновляет `transaction`
   * и может застать переход на `paymentUrl` ещё не завершённым (CPAYMENT-1846).
   */
  useEffect(() => {
    if (earlyIntentSentRef.current || !transaction) return;
    if (!REQUISITE_STATUSES.includes(transaction.status) || !isSupportedMethod || isRedirectMethod) return;
    // Короткое окно есть только у тикетов с обязательным чеком; остальным отметка ничего не
    // продлевает, зато переключила бы экран таймаута на «время истекло».
    if (transaction.simpleConfirmation !== false) return;
    if (transaction.receiptIntent) {
      earlyIntentSentRef.current = true;
      return;
    }

    earlyIntentSentRef.current = true;
    setShowConfirm(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [transaction?.status, transaction?.simpleConfirmation, transaction?.receiptIntent, isSupportedMethod, isRedirectMethod]);

  const handleConfirmWithReceipt = async () => {
    try {
      await onConfirm();
      setReceiptAccepted(true);
      setProcessingAfterUpload(true);
    } catch {
      setServerError(true);
    }
  };

  // CPAYMENT-1616: re-upload in FILES_AUTO_VERIFICATION replaces the file server-side (no re-confirm —
  // the ticket is not in PAYMENT_PENDING). Show the wait screen until the poll returns the fresh phase.
  const handleReupload = async () => {
    setReuploading(true);
  };

  useEffect(() => {
    if (transaction?.receiptVerification?.phase === 'pending') {
      setReuploading(false);
    }
  }, [transaction?.receiptVerification?.phase]);

  // ─── Loading ────────────────────────────────────────────────────────────────
  if (loading) {
    return <Spinner />;
  }

  // ─── Ожидание кончилось ничем (CPAYMENT-1716) ───────────────────────────────
  if (timeoutScreen && transaction) {
    return timeoutScreen === 'payment-expired' ? (
      <OnePagePaymentExpiredScreen
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onContactSupport={() => onRedirect(supportTarget(transaction))}
      />
    ) : (
      <OnePageReceiptUnconfirmedScreen
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onContactSupport={() => onRedirect(supportTarget(transaction))}
      />
    );
  }

  // ─── Error / no transaction ─────────────────────────────────────────────────
  if (error || !transaction) {
    return (
      <OnePageServerErrorScreen
        transaction={transaction!}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onReturn={() => transaction && onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Redirect-url paymentUrl in flight → bare spinner until navigation ──────
  if (
    transaction.requisite?.type === 'redirect_url' &&
    transaction.requisite?.paymentUrl
  ) {
    return <Spinner description={t('waiting.searchingRequisites')} />;
  }

  // ─── Выбор суммы депозита ───────────────────────────────────────────────────
  if (transaction.status === 'WAITING_FOR_VARIANT_SELECTION') {
    return (
      <OnePageVariantPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formatTime(timeLeft || 0)}
        onDeclineOffer={onDeclineOffer}
        onSelectVariant={onSelectVariant}
        variantSubmitting={variantSubmitting}
      />
    );
  }

  if (transaction.status === 'WAITING_FOR_REQUISITE') {
    return <Spinner description={t('waiting.searchingRequisites')} />;
  }

  // ─── CLIENT_PENDING — adapter not found ─────────────────────────────────────
  if (
    transaction.status === 'CLIENT_PENDING' &&
    !transaction.requisite?.type
  ) {
    if (!isSupportedMethod) {
      // Non-SBP/Card: bare spinner during retry phase; final state redirects via useEffect above.
      return <Spinner description={t('waiting.searchingRequisites')} />;
    }
    if ((transaction as any).paymentAdapterSearchAttemptsCounter > 0) {
      return (
        <OnePageRetryPage
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onRetry={() => setRetry(prev => prev + 1)}
          onBack={() => onRedirect(transaction.redirectUrl)}
        />
      );
    }
    return (
      <OnePageErrorScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onFinish={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Success ────────────────────────────────────────────────────────────────
  if (transaction.status === 'COMPLETED') {
    return (
      <OnePageSuccessPage
        transaction={transaction}
        uuid={uuid || ''}
        onRedirect={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Files verification (final for client) ─────────────────────────────────
  if (transaction.status === 'FILES_VERIFICATION') {
    if (transaction.receiptVerification?.phase === 'unconfirmed') {
      return (
        <OnePageReceiptUnconfirmedScreen
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onContactSupport={() => onRedirect(supportTarget(transaction))}
        />
      );
    }
    return (
      <OnePageSuccessPage
        transaction={transaction}
        uuid={uuid || ''}
        onRedirect={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Receipt auto-verification (CPAYMENT-1616) ──────────────────────────────
  if (transaction.status === 'FILES_AUTO_VERIFICATION') {
    const rv = transaction.receiptVerification;
    // Failed check → let the client re-upload (no re-confirm). Hide the stale "failed" while
    // the just-uploaded file is being picked up by the next poll.
    if (rv?.phase === 'failed' && rv.canReupload && !reuploading) {
      return (
        <OnePageUploadPage
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onConfirm={handleReupload}
          onBack={() => onRedirect(transaction.redirectUrl)}
          reupload
        />
      );
    }
    return (
      <OnePageProcessingScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onBack={() => onRedirect(transaction.redirectUrl)}
        receiptAccepted={false}
      />
    );
  }

  // ─── Чек ушёл на разбор: ждём, пока тикет доедет до финального статуса ──────
  if (processingAfterUpload) {
    if (serverError) {
      return (
        <OnePageServerErrorScreen
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onReturn={() => onRedirect(transaction.redirectUrl)}
        />
      );
    }
    if (transaction.status === 'DECLINED' || transaction.status === 'EXPIRED') {
      return (
        <OnePageErrorScreen
          transaction={transaction}
          uuid={uuid || ''}
          timeLeft={formattedTime}
          onFinish={() => onRedirect(transaction.redirectUrl)}
          receiptAccepted={receiptAccepted}
        />
      );
    }
    return (
      <OnePageProcessingScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onBack={() => setProcessingAfterUpload(false)}
      />
    );
  }

  // ─── Declined ───────────────────────────────────────────────────────────────
  if (transaction.status === 'DECLINED') {
    return (
      <OnePageErrorScreen
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onFinish={() => onRedirect(transaction.redirectUrl)}
        receiptAccepted={receiptAccepted}
      />
    );
  }

  // ─── Expired ────────────────────────────────────────────────────────────────
  if (transaction.status === 'EXPIRED') {
    return (
      <OnePageExpiredScreen
        transaction={transaction}
        uuid={uuid || ''}
        onFinish={() => onRedirect(transaction.redirectUrl)}
      />
    );
  }

  // ─── Единственный экран оплаты: реквизиты + чек ─────────────────────────────
  if (REQUISITE_STATUSES.includes(transaction.status) && isSupportedMethod) {
    return (
      <OnePagePaymentPage
        transaction={transaction}
        uuid={uuid || ''}
        timeLeft={formattedTime}
        onCancel={onCancel}
        onDeclineOffer={onDeclineOffer}
        cancelReasons={cancelReasons}
        onLeave={onLeave}
        receiptIntent={receiptIntent}
        lowTimeWarning={lowTimeWarning}
        onExtendTime={onExtendTime}
        onDismissLowTime={onDismissLowTime}
        onConfirm={handleConfirmWithReceipt}
      />
    );
  }

  // Fallback spinner for unhandled states
  return <Spinner />;
};

export default LionOnePageTemplate;
